import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Flame, BarChart3, ChevronDown, Loader2, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardROI({ roi: initialRoi }) {
  const [customers, setCustomers] = useState('');
  const [jobValue, setJobValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const roi = initialRoi;
  const currentMonth = roi.current_month;
  const subCost = roi.subscription_cost || 39;
  const defaultJobVal = roi.default_job_value || 300;
  const autoLeads = roi.auto_tracked_leads_this_month || 0;
  const cumulative = roi.cumulative || {};
  const monthlyData = roi.monthly_summary || [];

  const currentMonthData = monthlyData.find(m => m.month === currentMonth);

  useEffect(() => {
    if (currentMonthData) {
      setCustomers(String(currentMonthData.manual_new_customers || ''));
      setJobValue(String(currentMonthData.average_job_value || defaultJobVal));
    } else {
      setJobValue(String(defaultJobVal));
    }
  }, [currentMonthData, defaultJobVal]);

  const manualCount = parseInt(customers) || 0;
  const avgJob = parseFloat(jobValue) || defaultJobVal;
  const totalCustomers = manualCount + autoLeads;
  const revenue = totalCustomers * avgJob;
  const roiMult = subCost > 0 ? Math.round((revenue - subCost) / subCost) : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/roi/update`, {
        month: currentMonth,
        manual_new_customers: manualCount,
        average_job_value: avgJob,
      }, { withCredentials: true });
      setSavedData(res.data);
      setTimeout(() => setSavedData(null), 3000);
    } catch {}
    setSaving(false);
  };

  const monthLabel = (() => {
    const [y, m] = currentMonth.split('-');
    const d = new Date(parseInt(y), parseInt(m) - 1);
    return d.toLocaleString('default', { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] mb-4" style={{ border: '1px solid #D4C9B8' }} data-testid="roi-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-none flex items-center justify-center" style={{ backgroundColor: 'rgba(198,122,92,0.06)' }}>
            <DollarSign size={16} strokeWidth={1.5} style={{ color: '#C67A5C' }} />
          </div>
          <div>
            <h3 className="text-[13px] font-medium" style={{ color: '#3C3226' }}>ROI Tracker</h3>
            <p className="text-[12px]" style={{ color: '#8C7E6E' }}>{monthLabel}</p>
          </div>
        </div>
        {roiMult > 0 && totalCustomers > 0 && (
          <div className="flex items-center gap-1 px-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(29,158,117,0.06)' }} data-testid="roi-multiplier">
            <Flame size={14} style={{ color: '#C67A5C' }} />
            <span className="text-sm font-bold" style={{ color: '#1D6B4F' }}>{roiMult}x ROI</span>
          </div>
        )}
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <label className="text-[12px] mb-1 block" style={{ color: '#8C7E6E' }}>New customers from AI/online this month</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              value={customers}
              onChange={e => setCustomers(e.target.value)}
              placeholder="0"
              className="flex-1 rounded-none px-3 py-2.5 text-sm outline-none"
              style={{ border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF', color: '#3C3226' }}
              data-testid="roi-customers-input"
            />
            {autoLeads > 0 && (
              <span className="text-[12px] whitespace-nowrap" style={{ color: '#8C7E6E' }}>+ {autoLeads} auto-tracked</span>
            )}
          </div>
        </div>

        <div>
          <label className="text-[12px] mb-1 block" style={{ color: '#8C7E6E' }}>
            Average job value <span style={{ color: '#8C7E6E' }}>(default: ${defaultJobVal})</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#8C7E6E' }}>$</span>
            <input
              type="number"
              min="0"
              value={jobValue}
              onChange={e => setJobValue(e.target.value)}
              className="w-full rounded-none pl-7 pr-3 py-2.5 text-sm outline-none"
              style={{ border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF', color: '#3C3226' }}
              data-testid="roi-job-value-input"
            />
          </div>
        </div>
      </div>

      {totalCustomers > 0 && (
        <div className="p-3 rounded-none mb-3 space-y-2" style={{ backgroundColor: '#F7F4EF' }} data-testid="roi-results">
          <div className="flex justify-between text-[12px]">
            <span style={{ color: '#8C7E6E' }}>Estimated revenue</span>
            <span className="font-medium" style={{ color: '#3C3226' }}>${revenue.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-[12px]">
            <span style={{ color: '#8C7E6E' }}>Your investment</span>
            <span className="font-medium" style={{ color: '#3C3226' }}>${subCost}/mo</span>
          </div>
          <div className="pt-2 flex justify-between text-[12px]" style={{ borderTop: '1px solid #D4C9B8' }}>
            <span style={{ color: '#8C7E6E' }}>Return on investment</span>
            <span className="font-bold" style={{ color: '#1D6B4F' }}>{roiMult}x</span>
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full rounded-none text-[#F0EBE3] py-2.5 text-[12px] font-medium transition-opacity hover:opacity-85 disabled:opacity-60 flex items-center justify-center gap-1.5"
        style={{ backgroundColor: '#2C2520' }}
        data-testid="roi-save-btn"
      >
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
        {savedData ? 'Saved!' : 'Save this month'}
      </button>

      {monthlyData.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-[12px] transition-colors"
            style={{ color: '#8C7E6E' }}
            data-testid="roi-expand-cumulative"
          >
            <BarChart3 size={12} />
            <span>Past {monthlyData.length} month{monthlyData.length > 1 ? 's' : ''} summary</span>
            <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-3 rounded-none space-y-1.5" style={{ backgroundColor: '#F7F4EF' }} data-testid="roi-cumulative-summary">
                  <div className="flex justify-between text-[12px]">
                    <span style={{ color: '#8C7E6E' }}>Total customers</span>
                    <span className="font-medium" style={{ color: '#3C3226' }}>{cumulative.total_customers || 0}</span>
                  </div>
                  <div className="flex justify-between text-[12px]">
                    <span style={{ color: '#8C7E6E' }}>Total revenue</span>
                    <span className="font-medium" style={{ color: '#3C3226' }}>${(cumulative.total_revenue || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-[12px]">
                    <span style={{ color: '#8C7E6E' }}>Total invested</span>
                    <span className="font-medium" style={{ color: '#3C3226' }}>${(cumulative.total_investment || 0).toLocaleString()}</span>
                  </div>
                  <div className="pt-1.5 flex justify-between text-[12px]" style={{ borderTop: '1px solid #D4C9B8' }}>
                    <span style={{ color: '#8C7E6E' }}>Cumulative ROI</span>
                    <span className="font-bold" style={{ color: '#1D6B4F' }}>{cumulative.roi_multiplier || 0}x</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
